import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import useCharterBundlePricing from 'hooks/CharterFlights/useCharterBundlePricing'
import type { CharterBundlePricingInput } from 'hooks/CharterFlights/useCharterBundlePricing'

interface Props {
  offer: App.Offer | App.OfferSummary
  pricing: CharterBundlePricingInput
  isPricePerPerson?: boolean
  nights?: number
}

function CharterBundlePricing({ offer, pricing, isPricePerPerson, nights }: Props) {
  const intl = useIntl()
  const {
    displayPrice,
    memberDisplayPrice,
    valuePrice,
    discountPercentage: resolvedDiscountPercentage,
    guestCount,
    showMemberPricing,
  } = useCharterBundlePricing(pricing, isPricePerPerson)

  const priceElement = useMemo(() => {
    if (showMemberPricing) {
      return (
        <LuxPlusPriceStack
          price={displayPrice ?? 0}
          memberPrice={memberDisplayPrice}
          saleUnit={isPricePerPerson ? 'person' : 'total'}
          size="L"
        />
      )
    }
    return (
      <PriceRowPrice
        size="L"
        price={displayPrice ?? 0}
        saleUnit={isPricePerPerson ? 'person' : undefined}
      />
    )
  }, [showMemberPricing, displayPrice, memberDisplayPrice, isPricePerPerson])

  const roomCount = pricing.roomCount ?? 1
  const roomCaption = isPricePerPerson
    ? intl.formatMessage(
        {
          id: 'Uv5Ws_kAoNFVhFWlWnig',
          defaultMessage: '{guestCount, plural, one {# person} other {# people}}',
          description: 'Guest count caption for charter bundle pricing per person',
        },
        { guestCount },
      )
    : intl.formatMessage(
        {
          id: '94mAZcswvt-huk62Hj67',
          defaultMessage: '{roomCount, plural, one {# room} other {# rooms}}',
          description: 'Room count caption for charter bundle pricing total',
        },
        { roomCount },
      )

  // TODO: i18n — complex pricing caption with trailing ' from' and ' & flights' fragments
  const nightsAndFlightsFromLabel = nights
    ? intl.formatMessage(
        {
          id: 'FyZfb0XtR5FizQdNr4_Q',
          defaultMessage: '{resolvedNights, plural, one {# night} other {# nights}} & flights from',
          description: 'Charter bundle price caption showing nights and flights',
        },
        { resolvedNights: nights },
      )
    : undefined
  const fromLabel = intl.formatMessage({
    id: 'JSMgIzowLxrZt6jlUcqy',
    defaultMessage: 'From',
    description: 'Charter bundle price caption prefix for per-person pricing',
  })
  const totalFromLabel = intl.formatMessage({
    id: '-J1OGWHAvDhuHC2juRhD',
    defaultMessage: 'Total from',
    description: 'Charter bundle price caption prefix for total pricing',
  })
  const priceCaption = nightsAndFlightsFromLabel ?? (isPricePerPerson ? fromLabel : totalFromLabel)

  return (
    <Group direction="vertical" gap={4} fullWidth>
      <PriceRowPriceCaption>
        {priceCaption}
      </PriceRowPriceCaption>
      {priceElement}
      {!!valuePrice && !!resolvedDiscountPercentage && resolvedDiscountPercentage > 0 && (
        <PriceRowValueDiscountWithCaption
          size="M"
          originalValue={valuePrice}
          discountPercentage={resolvedDiscountPercentage}
        />
      )}
      <PriceRowCaption>{roomCaption}</PriceRowCaption>
      <PriceRowTaxesAndFees />
      <PriceRowAgentHubCommission
        productType={offer.productType}
        offerId={offer.id}
      />
    </Group>
  )
}

export default CharterBundlePricing
